import PropTypes from 'prop-types';
import React from 'react';
import AnimateHeight from 'react-animate-height';
import { isZipBasedShipping } from 'vivino-js/helpers/countryHelpers';
import CartIcon from 'vivino-ui/icons/Cart';
import ShippingTimeIcon from 'vivino-ui/icons/ShippingTime';
import Region from 'vivino-ui/icons/WineFacts/Region';

import ZipCodeDrawer from 'vivino-ui/components/ZipCodeDrawer/ZipCodeDrawer.container';

import styles from './shippingPolicy.scss';

const SHIPPING_MESSAGE_UNFOLD_ANIMATION_DURATION = 300;

const ShippingPolicy = ({
  merchantShippingMessage,
  shippingEstimate,
  countryCode,
  quantityHasChanged,
  shippingPolicy,
  onZipValidated,
  shippingZipChanged,
  shippingZipCode,
  drawerIsOpen,
  errorMessage,
}) => {
  const isZipRequired = isZipBasedShipping(countryCode);
  const hasCart = merchantShippingMessage || false;
  const pricingPolicy = shippingPolicy?.pricing_policy;

  const getPricingFromQuantityStatus = () => {
    return quantityHasChanged
      ? pricingPolicy?.shipping_message_relative
      : pricingPolicy?.shipping_message_absolute;
  };

  const deliveryEstimateSummary = shippingPolicy?.shipping_estimate?.delivery_estimate_summary;

  const getNonZipBasedPolicies = () => {
    if (hasCart) {
      return {
        shippingEstimateMsg: shippingEstimate,
        pricingPolicyMsg: merchantShippingMessage,
      };
    }

    return {
      shippingEstimateMsg: deliveryEstimateSummary,
      pricingPolicyMsg: getPricingFromQuantityStatus(),
    };
  };

  const getZipBasedPolicies = () => {
    if (!shippingZipCode) {
      return { shippingEstimateMsg: '', pricingPolicyMsg: '' };
    }

    if (!hasCart) {
      return {
        shippingEstimateMsg: deliveryEstimateSummary,
        pricingPolicyMsg: getPricingFromQuantityStatus(),
      };
    }

    if (shippingZipChanged) {
      return {
        shippingEstimateMsg: deliveryEstimateSummary,
        pricingPolicyMsg: pricingPolicy?.shipping_message_absolute,
      };
    }

    return {
      shippingEstimateMsg: shippingEstimate,
      pricingPolicyMsg: merchantShippingMessage,
    };
  };

  const getPolicies = () => {
    return isZipRequired ? getZipBasedPolicies() : getNonZipBasedPolicies();
  };

  const { shippingEstimateMsg, pricingPolicyMsg } = getPolicies();

  const shippingPolicies = (
    <>
      {!!pricingPolicyMsg && (
        <div className={styles.shippingPolicyItem}>
          <span className={styles.icon}>
            <CartIcon />
          </span>
          <span>{pricingPolicyMsg}</span>
        </div>
      )}
      {!!shippingEstimateMsg && (
        <div className={styles.shippingPolicyItem}>
          <span className={styles.icon}>
            <ShippingTimeIcon />
          </span>
          <span>{shippingEstimateMsg}</span>{' '}
        </div>
      )}
    </>
  );

  if (!isZipRequired) {
    return <div className={styles.shippingPolicy}>{shippingPolicies}</div>;
  }

  return (
    <div className={styles.shippingPolicy}>
      <>
        <div className={styles.zipCaptureItem}>
          <div className={styles.icon}>
            <Region className={styles.regionIcon} />
          </div>
          <div className={styles.zipCodeDrawerWrapper}>
            <ZipCodeDrawer
              shippingZipCode={shippingZipCode}
              countryCode={countryCode}
              onZipValidated={onZipValidated}
              isOpen={!shippingZipCode}
            />
          </div>
        </div>
        <AnimateHeight
          duration={SHIPPING_MESSAGE_UNFOLD_ANIMATION_DURATION}
          height={drawerIsOpen ? 'auto' : 0}
        >
          {shippingPolicies}
        </AnimateHeight>
        {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
      </>
    </div>
  );
};

ShippingPolicy.propTypes = {
  merchantId: PropTypes.number,
  quantityHasChanged: PropTypes.bool,
  merchantShippingMessage: PropTypes.string,
  countryCode: PropTypes.string,
  shippingEstimate: PropTypes.string,
  shippingPolicy: PropTypes.object,
  onZipValidated: PropTypes.func,
  shippingZipChanged: PropTypes.bool,
  shippingZipCode: PropTypes.string,
  drawerIsOpen: PropTypes.bool,
  errorMessage: PropTypes.string,
};

export default ShippingPolicy;
